/**
 * Crop colour map — DOMAIN colormap, НЕ Earth Tech theme token.
 *
 * Каждой культуре — узнаваемый bg/border (как агроном видит на бумажной
 * карте севооборота: пшеница золотистая, кукуруза оранжевая, рис зелёный).
 * Это data-encoding палитра, не UI-тема: при ребрендинге Earth Tech НЕ
 * меняется, сравнимость между сезонами/хозяйствами зависит от стабильности.
 *
 * Whitelisted в `frontend-vite/CLAUDE.md` (hex разрешён в `src/lib/colorMaps/`).
 * Единый источник для дашборда севооборота, легенд, бейджей культур.
 */

export interface CropColor {
  /** Фон чипа/ячейки культуры */
  bg: string;
  /** Цвет рамки */
  border: string;
}

/** Substring-keyed: ключ матчится по `name.toLowerCase().includes(key)`. */
export const CROP_COLOR: Record<string, CropColor> = {
  пшеница: { bg: '#FFF9E6', border: '#E8D060' },
  рис: { bg: '#E8F5E9', border: '#81C784' },
  кукуруза: { bg: '#FFF3E0', border: '#FFB74D' },
  подсолн: { bg: '#FFF4B5', border: '#FFCA28' },
  соя: { bg: '#E8EAF6', border: '#7986CB' },
  рапс: { bg: '#F3E5F5', border: '#BA68C8' },
  ячмень: { bg: '#FFF4E0', border: '#D4A85C' },
  пар: { bg: '#F5F5F5', border: '#BDBDBD' },
  горох: { bg: '#F1F8E9', border: '#9CCC65' },
  лён: { bg: '#E1F5FE', border: '#4FC3F7' },
  свёкл: { bg: '#FFEBEE', border: '#E57373' },
};

/** Цвет для неизвестной / отсутствующей культуры. */
export const CROP_FALLBACK: CropColor = { bg: '#FAFAFA', border: '#E0E0E0' };

/**
 * Возвращает bg/border для культуры по её имени (substring-match,
 * case-insensitive). Пустое/неизвестное имя → CROP_FALLBACK.
 */
export function getCropColor(name?: string | null): CropColor {
  if (!name) return CROP_FALLBACK;
  const low = name.toLowerCase();
  for (const k of Object.keys(CROP_COLOR)) {
    if (low.includes(k)) return CROP_COLOR[k];
  }
  return CROP_FALLBACK;
}

/**
 * Цвет культуры по умолчанию — когда своего у хозяйства нет и системный тоже
 * не задан. Обязан совпадать с серверным дефолтом MVT-заливки
 * (`COALESCE(c.color, '#9CA3AF')` в tenant_template.sql): иначе карточка
 * справочника показывает один цвет, а карта рисует другой.
 */
export const DEFAULT_CROP_COLOR = '#9CA3AF';
